import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import SettingsBrightnessIcon from "@mui/icons-material/SettingsBrightness";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import NightsStayOutlinedIcon from "@mui/icons-material/NightsStayOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { QRCodeSVG } from "qrcode.react";
import { logout } from "../pages/auth/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { apiSlice } from "../../app/apiSlice";
import { useState } from "react";
import { useNetworkStatus } from "../../hooks/useNetworkStatus";
import { attemptOfflineAwareLogout } from "../../app/syncService";
import { setColorMode, selectColorMode } from "../../theme/themeSlice";
import { getApiBaseUrl } from "../../helpers/apiUrl";
import { useGetPublicSchoolInfoQuery } from "../../app/authApiSlice";
import { useGetSchoolAgentQuery } from "../pages/settings/school/schoolApiSlice";
import { formatTelLink, formatWhatsappLink } from "../../helpers/phoneUtils";
import {
  ContactSupport,
  SupportAgent,
  SupportAgentOutlined,
} from "@mui/icons-material";

const DEFAULT_CONTACT_PHONE = "+234 8088 885 123";

function Header({ drawerWidth, handleDrawerToggle }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [helpAnchor, setHelpAnchor] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const helpOpen = Boolean(helpAnchor);
  const { isOnline } = useNetworkStatus();
  const colorMode = useSelector(selectColorMode);
  const { data: publicSchoolInfo } = useGetPublicSchoolInfoQuery();
  const qrPayload = JSON.stringify({
    type: "schoola-add",
    url: getApiBaseUrl(),
  });

  const modeSequence = ["system", "light", "dark"];
  const handleModeToggle = () => {
    const next =
      modeSequence[(modeSequence.indexOf(colorMode) + 1) % modeSequence.length];
    dispatch(setColorMode(next));
  };

  const { data: agent } = useGetSchoolAgentQuery();

  const agentPhone = agent?.profile?.phone || DEFAULT_CONTACT_PHONE;

  // Contact information - easy to modify
  const CONTACT_INFO = {
    phone: {
      display: agentPhone,
      link: formatTelLink(agentPhone),
      icon: "📞",
    },
    whatsapp: {
      display: agentPhone,
      link: formatWhatsappLink(agentPhone),
      icon: "💬",
    },
    email: {
      display: agent?.profile?.email || "support@schoola.app",
      link: agent?.profile?.email
        ? `mailto:${agent.profile.email}`
        : "mailto:support@schoola.app",
      icon: "✉️",
    },
  };

  const modeIcon =
    colorMode === "light" ? (
      <WbSunnyOutlinedIcon fontSize="small" />
    ) : colorMode === "dark" ? (
      <NightsStayOutlinedIcon fontSize="small" />
    ) : (
      <SettingsBrightnessIcon fontSize="small" />
    );

  const modeLabel =
    colorMode === "light"
      ? "Light mode"
      : colorMode === "dark"
        ? "Dark mode"
        : "System default";

  const handleLogout = async () => {
    const result = await attemptOfflineAwareLogout();
    if (!result.allowed) {
      const discard = window.confirm(
        "You have unsynced offline report changes on this device. Stay signed in to sync them later, or press OK to discard them and continue logging out.",
      );
      if (!discard) return;
      await attemptOfflineAwareLogout({ forceDiscard: true });
    }
    dispatch(logout());
    dispatch(apiSlice.util.resetApiState());
    navigate("/login");
  };

  const handleHelpOpen = (event) => {
    setHelpAnchor(event.currentTarget);
  };

  const handleHelpClose = () => {
    setHelpAnchor(null);
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={(theme) => ({
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          boxShadow: "none",
          backgroundColor:
            theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f1f1",
          borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#333" : "#e7e7e7"}`,
        })}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1, display: { sm: "none" } }}
          >
            <MenuIcon sx={{ color: "primary.safeOnLight" }} />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}></Box>

          {/* Offline indicator */}
          {!isOnline && (
            <Tooltip title="You are offline. Changes are being saved locally and will sync when you reconnect.">
              <Chip
                icon={<WifiOffIcon sx={{ fontSize: 16 }} />}
                label="Offline"
                size="small"
                color="warning"
                variant="filled"
                sx={{ mr: 1, fontWeight: 600, cursor: "default" }}
              />
            </Tooltip>
          )}

          {/* LMS Button */}
          <IconButton
            onClick={() => navigate("/lms/overview")}
            sx={{
              mr: { xs: 0.5, sm: 1 },
              borderRadius: 1,
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "primary.safeOnLight",
            }}
            title="LMS"
          >
            LMS
          </IconButton>

          {/* Mobile app QR code button */}
          <Tooltip title="Add school to Schoola mobile app">
            <IconButton
              onClick={() => setShowQr(true)}
              sx={{ mr: { xs: 0.5, sm: 1 }, color: "primary.safeOnLight" }}
              aria-label="Add to mobile app"
            >
              <QrCode2Icon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Dark / Light / System mode toggle */}
          <Tooltip title={modeLabel}>
            <IconButton
              onClick={handleModeToggle}
              sx={{ mr: { xs: 0.5, sm: 1 }, color: "primary.safeOnLight" }}
              aria-label="Toggle colour mode"
            >
              {modeIcon}
            </IconButton>
          </Tooltip>

          {/* Help Button */}
          <IconButton
            onClick={handleHelpOpen}
            sx={{ mr: { xs: 0.5, sm: 1 } }}
            title="Help"
          >
            <SupportAgent sx={{ color: "primary.safeOnLight" }} />
          </IconButton>

          {/* Help Menu */}
          <Menu
            anchorEl={helpAnchor}
            open={helpOpen}
            onClose={handleHelpClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem disabled sx={{ cursor: "default" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Need Help?
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => (window.location.href = CONTACT_INFO.phone.link)}
              sx={{ fontSize: "0.9rem" }}
            >
              {CONTACT_INFO.phone.icon} Phone: {CONTACT_INFO.phone.display}
            </MenuItem>
            <MenuItem
              onClick={() => window.open(CONTACT_INFO.whatsapp.link, "_blank")}
              sx={{ fontSize: "0.9rem" }}
            >
              {CONTACT_INFO.whatsapp.icon} WhatsApp:{" "}
              {CONTACT_INFO.whatsapp.display}
            </MenuItem>
            <MenuItem
              onClick={() => (window.location.href = CONTACT_INFO.email.link)}
              sx={{ fontSize: "0.9rem" }}
            >
              {CONTACT_INFO.email.icon} Email: {CONTACT_INFO.email.display}
            </MenuItem>
          </Menu>

          {/* Logout Button */}
          <Box
            sx={{
              height: 32,
              width: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#fff",
              borderRadius: 10,
            }}
          >
            <PowerSettingsNewIcon
              sx={{
                color: "primary.safeOnLight",
                cursor: "pointer",
                "& :hover": {
                  color: "red",
                },
              }}
              onClick={handleLogout}
            />
          </Box>
        </Toolbar>
      </AppBar>

      {/* QR Dialog */}
      <Dialog
        open={showQr}
        onClose={() => setShowQr(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Add School to Mobile App</span>
          <IconButton size="small" onClick={() => setShowQr(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack alignItems="center" spacing={2} sx={{ py: 1 }}>
            {publicSchoolInfo?.logo && (
              <Avatar
                src={publicSchoolInfo.logo}
                alt={publicSchoolInfo.name}
                sx={{ width: 48, height: 48 }}
              />
            )}
            {publicSchoolInfo?.name && (
              <Typography variant="subtitle1" fontWeight="bold">
                {publicSchoolInfo.name}
              </Typography>
            )}
            <Box
              sx={{
                p: 2,
                border: "1px solid #eee",
                borderRadius: 2,
                backgroundColor: "#fff",
              }}
            >
              <QRCodeSVG value={qrPayload} size={200} />
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              Open the <strong>Schoola</strong> app, tap{" "}
              <strong>Add a School</strong>, then scan this QR code.
            </Typography>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default Header;
